import React, { useState, useEffect, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import Loader from "./Sub_components/Loader";
import PoorConnectionError from "./Screens/PoorConnectionError";
import FeedbackAttendee from "./Components/FeedbackAttendee";
import SaveRecordingScreen from "./Screens/SaveRecordingScreen";
import ReviewScreen from "./Components/ReviewScreen";
import WaitingForHostToJoin from "./Components/WaitingForHostToJoin";
import Error from "./Components/Error";
import FeedbackHost from "./Components/FeedbackHost";
import HostAttendeePreviewScreen from "./Screens/PreviewScreen";
import ClassSessionScreen from "./Screens/ClassSessionScreen";
import Information from "./Components/Information";
import { useLiveStreamAppContext } from "./ContextStore/Store"; // Import the existing context
import {
  CallClientProvider,
  createStatefulCallClient,
} from "@azure/communication-react";
import DismissComp from "./Components/DismissComp";
import RejoinComp from "./Components/RejoinComp";
import JoinLiveStreamApp from "./Screens/JoinLiveStreamApp";
import FetchPlayerSettings from "./GeneralFunctions/FetchPlayerSettings";
import StartLiveStream from "./GeneralFunctions/StartLiveStream";
import FetchRoomDetail from "./GeneralFunctions/FetchRoomDetail";

const App = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [vertualFocusedContent, setVertualFocusedContent] = useState(false);
  const [isContextualMenuOpen, setIsContextualMenuOpen] = useState(false);
  const {
    isNetworkError,
    setIsNetworkError,
    loaderAnimation,
    errorMessage,
    communicationUserId,
    statefulCallClient,
    setStatefulCallClient,
    showInformationStrip,
    setShowInformationStrip,
    showIframe,
    informationContent,
    isReciveingOrShareingScreenShare,
    serviceProviderId,
    setLoaderAnimation,
    role,
    recordedFilePath,
    setReferringStageDomain,
  } = useLiveStreamAppContext();
  const { message } = informationContent;
  const { startClassCall } = StartLiveStream();
  const { getplayersettings } = FetchPlayerSettings();
  const { roomdetail } = FetchRoomDetail();

  useEffect(() => {
    const htmlTag = document.documentElement; // Reference to <html> tag

    if (showIframe) {
      htmlTag.classList.add("historypopupmobile");
    } else {
      htmlTag.classList.remove("historypopupmobile");
    }

    // Optional clean-up (if needed later)
    return () => {
      htmlTag.classList.remove("historypopupmobile");
    };
  }, [showIframe]); // Dependency array ensures this runs when `showIframe` changes

  //useEffect for First API call on page load
  useEffect(() => {
    if (role?.length > 0) {
      //Restrictions for Api Call on Page Load
      const currentPath = window.location.pathname;
      const newPath1 = currentPath.includes("/start");
      const newPath2 = currentPath.includes("/meeting");
      const newPath3 = currentPath.includes("/recording-playback");
      const newPath4 = currentPath.includes("/feedbackhost");
      const newPath5 = currentPath.includes("/feedback");
      const newPath6 = currentPath.includes("/review");

      if (
        newPath1 ||
        newPath2 ||
        newPath3 ||
        ((navigator.userAgent.indexOf("com.vagaro.iospro") > -1 ||
          navigator.userAgent.indexOf("com.vagaro.customerios") > -1) &&
          (newPath4 || newPath5 || newPath6))
      ) {
        roomdetail();
      } else {
        setLoaderAnimation(false);
      }
    }
  }, [role, recordedFilePath]);

  useEffect(() => {
    if (serviceProviderId?.length > 0) {
      getplayersettings().then(() => {
        const currentPath = window.location.pathname;
        const newPath = currentPath.includes("/meeting");
        const newPath3 = currentPath.includes("/recording-playback");
        if (newPath || newPath3) {
          startClassCall();
        }
      });
    }
  }, [serviceProviderId]);

  useEffect(() => {
    if (showInformationStrip) {
      const isExtendableMessage =
        message === "5 minutes left in your scheduled meeting." ||
        message === "1 minute left in your scheduled meeting.";
      if (!isExtendableMessage) {
        const timer = setTimeout(() => {
          //console.log("timer stop");
          setShowInformationStrip(false);
        }, 5000);
        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [showInformationStrip, message]);

  const checkConnection = () => {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection) {
      const downlink = connection.downlink;

      // Define poor connection criteria
      if (downlink < 0.1) {
        setIsNetworkError(true);
      } else {
        setIsNetworkError(false);
      }
    }
  };

  // Check network status (online/offline)
  const handleOffline = () => {
    setIsOnline(navigator.onLine);
    setIsNetworkError(true);
    // Pause or stop stream logic here
  };

  const handleOnline = () => {
    setIsOnline(navigator.onLine);
    setIsNetworkError(false);
    // Resume or reconnect stream logic here
  };

  // Initial check
  useEffect(() => {
    //Previous Referral URL: https://dev22.bookitall.com/
    const referrer = document.referrer;
    if (
      referrer.includes("bookitall.com") &&
      !referrer.includes("livestream.bookitall.com")
    ) {
      try {
        const url = new URL(referrer);
        const subDomain = url.hostname.split(".")[0];
        setReferringStageDomain(subDomain);
      } catch (e) {
        console.error("Invalid URL:", e);
        setReferringStageDomain("");
      }
    }

    // Initial connection quality check
    checkConnection();

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOnline);

    // Add listener for network changes
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection) {
      connection.addEventListener("change", checkConnection);
    }

    const handleBackButton = (e) => {
      e.preventDefault();
      window.location.reload();
    };
    window.addEventListener("popstate", handleBackButton);

    return () => {
      if (connection) {
        connection.removeEventListener("change", checkConnection);
      }
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("popstate", handleBackButton);
    };
  }, []);

  useEffect(() => {
    if (communicationUserId) {
      const statefulCallClient = createStatefulCallClient({
        userId: { communicationUserId: communicationUserId },
      });
      setStatefulCallClient(statefulCallClient);
    }
  }, [communicationUserId]);
  return (
    <>
      {loaderAnimation && <Loader />}
      {showInformationStrip && <Information />}
      {errorMessage && <Error />}
      {(isNetworkError || !isOnline) && !errorMessage ? (
        <PoorConnectionError checkConnection={checkConnection} />
      ) : (
        <div
          className={`${
            new URLSearchParams(window.location.search).get("role") === "h"
              ? "HostSide"
              : "AttendeeSide"
          }`}
        >
          <div
            className={`player-gray-bg ${
              isReciveingOrShareingScreenShare &&
              vertualFocusedContent === false
                ? "screentop-space"
                : ""
            } ${isContextualMenuOpen ? "menu-overlay" : ""} `}
          >
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route
                  path="/livestream/joinlivestreamapp"
                  element={<JoinLiveStreamApp />}
                />
                <Route
                  path="/livestream/start"
                  element={<HostAttendeePreviewScreen />}
                />
                <Route
                  path="/livestream/meeting"
                  element={
                    statefulCallClient ? (
                      <CallClientProvider callClient={statefulCallClient}>
                        <ClassSessionScreen
                          vertualFocusedContent={vertualFocusedContent}
                          setVertualFocusedContent={setVertualFocusedContent}
                          setIsContextualMenuOpen={setIsContextualMenuOpen}
                        />
                      </CallClientProvider>
                    ) : (
                      ""
                    )
                  }
                />
                <Route
                  path="/livestream/recording-playback"
                  element={<SaveRecordingScreen />}
                />
                <Route
                  path="/livestream/feedback"
                  element={<FeedbackAttendee />}
                />
                <Route
                  path="/livestream/feedbackhost"
                  element={<FeedbackHost />}
                />
                <Route path="/livestream/dismiss" element={<DismissComp />} />
                <Route
                  path="/livestream/rejoindismiss"
                  element={<RejoinComp />}
                />
                <Route path="/livestream/review" element={<ReviewScreen />} />
                <Route
                  path="/livestream/hostnotjoined"
                  element={<WaitingForHostToJoin />}
                />
                <Route path="/livestream/error" element={<Error />} />
              </Routes>
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

export default App;
